import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listPendingPayments } from "@/lib/api";
import { fmtMoney } from "@/lib/money";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Clock, ChevronRight, BellRing, X } from "lucide-react";

const STORAGE_KEY = "pending_payments_dismissed_at";
const REMINDER_INTERVAL_HOURS = 6;

// UI dismiss timestamp only — no sensitive data. sessionStorage over localStorage so it doesn't
// linger across browser sessions (satisfies the "no persistent client storage" audit rule).
const _readDismissedAt = () => {
  try { return Number(sessionStorage.getItem(STORAGE_KEY) || 0); } catch { return 0; }
};
const _writeDismissedAt = () => {
  try { sessionStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
};
// Phones: the floating bell can be closed (until FPA is next opened, or the next 6-hourly reminder).
const PILL_HIDDEN_KEY = "pending_payments_pill_hidden";
const _readPillHidden = () => {
  try { return sessionStorage.getItem(PILL_HIDDEN_KEY) === "1"; } catch { return false; }
};
const _writePillHidden = (v) => {
  try { v ? sessionStorage.setItem(PILL_HIDDEN_KEY, "1") : sessionStorage.removeItem(PILL_HIDDEN_KEY); } catch { /* ignore */ }
};

/**
 * Auto-popup dashboard reminder for outstanding partial payments.
 * - Auto-opens once every 6 hours while there's an overdue OR upcoming-in-14-days balance.
 * - The floating pill button always sits on screen so anyone can pull the list up on demand.
 */
export function PendingPaymentsReminder() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pillHidden, setPillHidden] = useState(_readPillHidden);

  useEffect(() => {
    let alive = true;
    listPendingPayments()
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const shouldNag = (data.counts?.overdue || 0) > 0 || (data.counts?.upcoming || 0) > 0;
    if (!shouldNag) return;
    const gap = Date.now() - _readDismissedAt();
    if (gap > REMINDER_INTERVAL_HOURS * 3600 * 1000) {
      setOpen(true);
      setPillHidden(false);          // the reminder brings the bell back
      _writePillHidden(false);
    }
  }, [data]);

  const dismiss = () => {
    _writeDismissedAt();
    setOpen(false);
  };

  const totalCount = data?.counts?.total || 0;
  const overdueCount = data?.counts?.overdue || 0;

  if (!data || totalCount === 0) return null;

  return (
    <>
      {/* Floating pill. Desktop: bottom-right. Phones: bottom-left above the bottom bar
          (clear of the New lead / New Sale button), with an X to close it. */}
      {!pillHidden && (
        <div className={`fixed z-40 inline-flex items-center rounded-full shadow-xl border transition-all
          bottom-[calc(5rem+env(safe-area-inset-bottom))] left-4 lg:left-auto lg:bottom-6 lg:right-6 ${
          overdueCount > 0
            ? "bg-rose-500/95 border-rose-300 text-white"
            : "bg-amber-500/95 border-amber-300 text-black"
        }`}>
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 pl-3.5 pr-2 lg:px-4 py-2.5 rounded-full hover:brightness-110"
            data-testid="pending-payments-pill"
            title={`${totalCount} pending payment(s) · ${overdueCount} overdue`}
          >
            <BellRing className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em]">
              {overdueCount > 0 ? `${overdueCount} overdue` : `${totalCount} pending`}
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setPillHidden(true); _writePillHidden(true); }}
            className="lg:hidden mr-1 p-1.5 rounded-full hover:bg-black/15"
            aria-label="Hide pending payments reminder"
            data-testid="pending-payments-pill-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <PendingPaymentsDialog
        open={open}
        onClose={dismiss}
        data={data}
        onOpenLead={(leadId) => { dismiss(); navigate(`/sales?lead=${leadId}`); }}
      />
    </>
  );
}

export function PendingPaymentsDialog({ open, onClose, data, onOpenLead }) {
  const [tab, setTab] = useState("overdue");
  const list = useMemo(() => {
    if (!data) return [];
    if (tab === "overdue") return data.overdue || [];
    if (tab === "upcoming") return data.upcoming || [];
    if (tab === "started") return (data.all || []).filter((r) => r.started && !r.on_plan);
    return data.all || [];
  }, [data, tab]);

  const tabs = [
    { key: "overdue", label: "Overdue", count: data?.counts?.overdue || 0, tone: "rose" },
    { key: "upcoming", label: "Due in 14 days", count: data?.counts?.upcoming || 0, tone: "amber" },
    { key: "all", label: "All pending", count: data?.counts?.total || 0, tone: "zinc" },
    ...(data?.counts?.started_owing ? [{ key: "started", label: "Training, still owes", count: data.counts.started_owing, tone: "rose" }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="pending-payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <BellRing className="h-4 w-4 text-amber-400" />
            Pending payments
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {data?.counts?.total || 0} lead(s) still owe money — total outstanding{" "}
            <span className="text-zinc-300 font-mono">{fmtMoney(data?.counts?.total_amount || 0)}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b border-[#1f1f1f] pb-2 mt-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.15em] transition-colors ${
                tab === t.key
                  ? (t.tone === "rose" ? "bg-rose-500/15 text-rose-200 border border-rose-500/40"
                    : t.tone === "amber" ? "bg-amber-500/15 text-amber-200 border border-amber-500/40"
                    : "bg-zinc-500/15 text-zinc-200 border border-zinc-500/40")
                  : "text-zinc-500 hover:text-zinc-300 border border-transparent"
              }`}
              data-testid={`pending-tab-${t.key}`}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        <div className="mt-3 overflow-y-auto flex-1">
          {list.length === 0 ? (
            <div className="text-center text-xs text-zinc-500 py-8">Nothing here — good work.</div>
          ) : (
            <div className="space-y-2">
              {(list || []).map((row) => <PendingRow key={row.lead_id} row={row} today={data?.today} onOpen={onOpenLead} />)}
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-[#1f1f1f] flex items-center justify-between gap-3">
          <div className="text-[11px] text-zinc-500">Reminder returns every {REMINDER_INTERVAL_HOURS}h until cleared.</div>
          <Button variant="outline" onClick={onClose} className="border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#141414]" data-testid="pending-close-btn">
            <X className="h-3.5 w-3.5 mr-1.5" /> Dismiss
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PendingRow({ row, today, onOpen }) {
  const isOverdue = row.due_date && today && row.due_date < today;
  const dayDiff = row.due_date && today ? Math.round((new Date(row.due_date) - new Date(today)) / 86400000) : null;

  return (
    <button
      onClick={() => onOpen(row.lead_id)}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        isOverdue
          ? "bg-rose-500/5 border-rose-500/30 hover:border-rose-500/60"
          : "bg-[#0a0a0a] border-[#262626] hover:border-[#3a3a3a]"
      }`}
      data-testid={`pending-row-${row.lead_id}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-medium text-zinc-100">
            {row.name}
            {row.plan === "annual" && (
              <span className="ml-2 rounded-full border border-amber-500/40 px-2 py-0.5 text-[10px] text-amber-300">Annual · payment {row.payments} of 3</span>
            )}
            {row.started && !row.on_plan && (
              <span className="ml-2 rounded-full border border-rose-500/50 bg-rose-500/10 px-2 py-0.5 text-[10px] font-semibold text-rose-300" data-testid="pending-started-flag">
                Training, not fully paid
              </span>
            )}
          </div>
          {row.started_unpaid?.reason && (
            <div className="text-[10px] text-rose-300/80 mt-0.5">Started anyway: “{row.started_unpaid.reason}” · {row.started_unpaid.by}</div>
          )}
          <div className="text-[10px] text-zinc-500 mt-0.5">
            {row.phone && <span className="font-mono">{row.phone}</span>}
            {row.programme && <span className="ml-2">· {row.programme}</span>}
            {row.sales_ex && <span className="ml-2">· with {row.sales_ex}</span>}
            {row.orientation_date && <span className="ml-2">· orientation {row.orientation_date}</span>}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-zinc-500" />
      </div>
      <div className="flex items-center justify-between gap-2 mt-2 text-xs">
        <div>
          <Badge className={`border ${isOverdue ? "bg-rose-500/10 text-rose-200 border-rose-500/40" : "bg-amber-500/10 text-amber-200 border-amber-500/40"}`}>
            {isOverdue ? <AlertTriangle className="inline h-3 w-3 mr-1" /> : <Clock className="inline h-3 w-3 mr-1" />}
            {row.due_date
              ? isOverdue
                ? `${Math.abs(dayDiff)}d overdue`
                : `Due in ${dayDiff}d · ${row.due_date}`
              : "No due date"}
          </Badge>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pending</div>
          <div className="font-mono font-semibold text-rose-300">{fmtMoney(row.pending)}</div>
          <div className="text-[10px] text-zinc-500">
            paid <span className="text-zinc-300">{fmtMoney(row.amount_paid)}</span> of{" "}
            <span className="text-zinc-300">{fmtMoney(row.amount_sold)}</span>
          </div>
        </div>
      </div>
      {row.note && <div className="mt-2 text-[10px] text-zinc-500 italic">{row.note}</div>}
    </button>
  );
}
